import { useNavigate } from "react-router";

import type { CommunityGroupV2Dto, SelfProjectDto } from "@/api/types";
import { Button } from "@/design-system/Button";
import { Heading } from "@/design-system/Heading";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { useSlug } from "@/hooks/useSlug";
import { OnboardingFlowCard } from "@/modules/onboarding-flow/components/OnboardingFlowCard";
import { OnboardingFlowActions, OnboardingFlowRoot } from "@/modules/onboarding-flow/components/OnboardingFlowLayout";
import { OnboardingGroup } from "@/modules/onboarding-flow/components/OnboardingGroup";
import { OnboardingTopBar } from "@/modules/onboarding-flow/components/OnboardingTopBar";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface OnboardingGroupsProps {
  groups: CommunityGroupV2Dto[];
  total: number;
  project: SelfProjectDto;
  onFollow: (payload: { id: string; groupType: CommunityGroupV2Dto["type"] }) => void;
  onUnfollow: (payload: { id: string; groupType: CommunityGroupV2Dto["type"] }) => void;
}
export function OnboardingGroups({
  groups,
  total,
  project,
  onFollow,
  onUnfollow,
}: OnboardingGroupsProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClickNext = () => {
    void navigate(
      routes.onboardingFlow.helpCategories({
        slug,
      }),
    );
  };

  return (
    <OnboardingFlowRoot>
      <OnboardingTopBar />
      <OnboardingFlowCard>
        <Stack gap="sm">
          <Heading level={3}>{t("page.onboarding.groups.title")}</Heading>
          <Text as="p">{t("page.onboarding.groups.description")}</Text>
          <Text variant="caption-emphasis" tone="secondary">
            {t("page.onboarding.groups.subtext", {
              count: total,
            })}
          </Text>
        </Stack>
        <Stack gap="sm">
          {groups.map((group) => (
            <OnboardingGroup
              key={group.id}
              group={group}
              projectDefaultImage={project.backgroundImage}
              onFollow={() => onFollow({ id: group.id, groupType: group.type })}
              onUnfollow={() => onUnfollow({ id: group.id, groupType: group.type })}
            />
          ))}
        </Stack>
        <OnboardingFlowActions>
          <Stack gap="sm">
            <Button data-testid="next-step-button" fullWidth onClick={handleClickNext}>
              {t("common.action.next")}
            </Button>
            <Button
              type="link"
              variant="soft"
              href={routes.onboardingFlow.autoTranslationSetting({
                slug,
              })}
              fullWidth
            >
              {t("common.action.back")}
            </Button>
          </Stack>
        </OnboardingFlowActions>
      </OnboardingFlowCard>
    </OnboardingFlowRoot>
  );
}
